import {
  DEFAULT_TODAY_WIDGET_LAYER,
  FINANCE_WIDGETS,
  PROJECT_WIDGETS,
  TODAY_WIDGETS,
  TODAY_WIDGET_LAYER_ORDER,
  type TodayWidgetDefinition,
  type TodayWidgetLayer,
} from '@scrambl/shared'

export type { TodayWidgetDefinition, TodayWidgetLayer }
export { TODAY_WIDGET_LAYER_ORDER }

/**
 * Layer assignments for widgets whose definitions live outside
 * `today-widgets.ts`. Both belong to the ambient `workspace` band, but their
 * definition files (`project-widgets.ts` / `finance-widgets.ts`) weren't in
 * scope when layers were added — fold these onto the definitions themselves
 * next time those files are touched.
 */
const LAYER_OVERRIDES: Record<string, TodayWidgetLayer> = {
  'project-timeline': 'workspace',
  'profit-loss': 'workspace',
}

const withLayer = (w: TodayWidgetDefinition): TodayWidgetDefinition => {
  const override = LAYER_OVERRIDES[w.id]
  return override ? { ...w, layer: override } : w
}

export const WIDGET_DEFINITIONS: TodayWidgetDefinition[] = [
  ...PROJECT_WIDGETS,
  ...TODAY_WIDGETS,
  ...FINANCE_WIDGETS,
].map(withLayer)

/** Layer a widget is ordered under. Unknown / undeclared widgets fall to `work`. */
export const getWidgetLayer = (widgetId: string): TodayWidgetLayer =>
  WIDGET_DEFINITIONS.find(w => w.id === widgetId)?.layer ??
  DEFAULT_TODAY_WIDGET_LAYER

/* ===== Role-based default layouts =====
 *
 * A 20-person org's manager and a contractor used to land on the same nine
 * widgets. These sets only ever feed `generateDefaultLayout()` — a member with
 * a saved layout is never touched, and every widget stays addable from
 * "Add widgets" regardless of role.
 */
export type TodayDefaultRole = 'manager' | 'member' | 'contractor'

/**
 * Cross-org context a manager wants on day one but an individual contributor
 * has to scroll past. Removed from the `member` default set.
 */
const MANAGER_ONLY_DEFAULT_WIDGET_IDS: readonly string[] = [
  'team-pulse',
  'project-timeline',
  'activity',
]

/**
 * Contractors get the narrow "what am I doing and what do I log" set — an
 * explicit list rather than a subtraction, because everything else on the
 * default board is internal context they can't act on.
 */
const CONTRACTOR_DEFAULT_WIDGET_IDS: readonly string[] = [
  'todays-schedule',
  'time-tracker',
  'my-tasks',
  'favorites',
]

/** `isDefault` stays the manager baseline, so promotions/pickers are unchanged. */
const getManagerDefaultWidgetIds = (): string[] =>
  WIDGET_DEFINITIONS.filter(w => w.isDefault).map(w => w.id)

/**
 * Widget ids that seed a brand-new layout for this role, in definition order.
 * Account-type filtering (personal vs organization) is applied by the caller.
 */
export const getDefaultWidgetIds = (role: TodayDefaultRole): string[] => {
  if (role === 'contractor') {
    // Ordered by the definition list, not the constant, so the seeded layout
    // keeps the same main-then-sidebar shape as the other roles.
    const wanted = new Set(CONTRACTOR_DEFAULT_WIDGET_IDS)
    return WIDGET_DEFINITIONS.filter(
      w => wanted.has(w.id) && w.isActive !== false,
    ).map(w => w.id)
  }

  const managerIds = getManagerDefaultWidgetIds()
  if (role === 'manager') return managerIds

  const excluded = new Set(MANAGER_ONLY_DEFAULT_WIDGET_IDS)
  return managerIds.filter(id => !excluded.has(id))
}
